import Link from "next/link"
import { Star } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Card, CardContent } from "@/components/ui/card"
import { cn, firstName } from "@/lib/utils"
import type { RatingTarget } from "@/lib/rating-targets"

/**
 * Card "Avalie a carona".
 *
 * Existe como componente único porque as DUAS surfaces que oferecem a
 * avaliação — `/trip/[id]` (detalhe da carona) e `/trip-summary/[id]`
 * (resumo da viagem) — precisam exatamente da mesma apresentação. Markup
 * duplicado aqui é markup que diverge com o tempo.
 *
 * A componente só DESENHA: quem pode avaliar quem e até quando mora em
 * `lib/rating-targets.ts` (dados) e `lib/my-rides.ts` (regra). O servidor
 * continua sendo a autoridade — o `POST /api/ratings` recheca tudo.
 *
 * - `role="driver"`: uma linha por passageiro CONFIRMADO ainda não avaliado,
 *   com o "+N" quando a pessoa ocupou mais de um assento.
 * - `role="passenger"`: uma linha só, com o motorista e o trecho do passageiro.
 */
export interface RatingPromptProps {
  rideId: string
  role: "driver" | "passenger"
  targets: RatingTarget[]
  /** Margem extra do card (a página decide o espaçamento do resto). */
  className?: string
}

export function RatingPrompt({ rideId, role, targets, className }: RatingPromptProps) {
  if (!targets || targets.length === 0) return null

  return (
    <Card className={cn("bg-white shadow-md overflow-hidden mb-6 border-blue-200", className)}>
      <CardContent className="p-4">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Star className="h-4 w-4 text-yellow-400" /> Avalie a carona
        </h3>

        {role === "driver" ? (
          <div className="space-y-2">
            {targets.map((target) => (
              <Link
                key={target.id}
                href={`/trip/${rideId}/rating?target=${target.id}`}
                className="flex items-center gap-3 rounded-lg border p-3 hover:bg-gray-50 transition-colors"
              >
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarImage src={target.avatar || undefined} alt={firstName(target.name)} />
                  <AvatarFallback>{target.name?.[0] || "P"}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 truncate">
                    {firstName(target.name)}
                    {target.count > 1 ? ` +${target.count - 1}` : ""}
                  </p>
                  {target.label && (
                    <p className="text-xs text-orange-600 font-medium truncate">{target.label}</p>
                  )}
                </div>
                <Star className="h-4 w-4 text-gray-400 shrink-0" />
              </Link>
            ))}
          </div>
        ) : (
          <Link
            href={`/trip/${rideId}/rating`}
            className="flex items-center gap-3 rounded-lg border p-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex-1 min-w-0">
              {targets[0].label && (
                <p className="text-xs text-orange-600 font-medium truncate">{targets[0].label}</p>
              )}
              <div className="flex items-center gap-2 mt-1">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={targets[0].avatar || undefined} alt={firstName(targets[0].name)} />
                  <AvatarFallback>{targets[0].name?.[0] || "M"}</AvatarFallback>
                </Avatar>
                <p className="font-medium text-gray-900 truncate">{firstName(targets[0].name)}</p>
              </div>
            </div>
            <Star className="h-4 w-4 text-gray-400 shrink-0" />
          </Link>
        )}
      </CardContent>
    </Card>
  )
}
